import { omit, isEmpty, toLower, uniqueId } from 'lodash';
import { axios } from './common';
import {
  CM_BASE_URL,
  AMLS_REPORT_BASE_URL,
  STR_POLLING_INTERVAL_MS,
  HISTORY_ITEMS_PER_PAGE
} from '../../pages/constants/cmConstants';
import {
  downloadFile,
  showFileDownloadingNotification,
  showFileDownloadSucessNotification,
  showFileDownloadFailedNotification,
  getFileNameFromContentDisposition
} from '../../pages/cm/utils/common';
import {
  convertFilterEpochForBackend,
  getLocalTimezone
} from '../../util/timezone';

export const fetchCases = async ({ filters, currentPage, pageSize }) => {
  const params = {
    'alert-level': filters.alertLevel,
    'assigned-user': !isEmpty(filters?.assignedUser)
      ? filters.assignedUser.join(',')
      : null,
    ...(filters.approvalStatus
      ? { 'checker-status': filters.approvalStatus }
      : ''),
    typologyIds: !isEmpty(filters.typologyIds)
      ? filters.typologyIds.join(',')
      : null,
    sources: !isEmpty(filters.sourceIds) ? filters.sourceIds.join(',') : null,
    ...omit(filters, [
      'alertLevel',
      'assignedUser',
      'searchField',
      'searchText',
      'typologyIds',
      'approvalStatus',
      'sourceIds'
    ]),
    offset: (currentPage - 1) * pageSize,
    limit: pageSize,
    from: convertFilterEpochForBackend(filters.from),
    to: convertFilterEpochForBackend(filters.to)
  };

  if (filters.searchField && filters.searchText) {
    params[filters.searchField] = toLower(filters.searchText);
  }

  const url = `${CM_BASE_URL}/cases/all`;
  const res = await axios.get(url, { params });

  if (res.status !== 200) {
    const error = res?.data?.error;

    throw { message: error ?? 'Failed to fetch cases' };
  }

  return res.data;
};

export const fetchCaseDetailsByCaseId = async (caseId) => {
  const url = `${CM_BASE_URL}/cases/byId/${caseId}`;
  const res = await axios.get(url);

  if (res.status !== 200) {
    throw new Error(res?.data?.error ?? 'Failed to fetch case details');
  }

  return res.data;
};

export const caseActionSubmit = async ({ caseActionDetails }) => {
  const url = `${CM_BASE_URL}/cases/takeActionOnCase`;

  const res = await axios.post(url, caseActionDetails);
  if (res.status !== 200) {
    const error = new Error(res.data?.msg || 'Failed to submit the action');
    error.response = res;
    throw error;
  }
  return res.data;
};

export const fetchCasesAllowedStatuses = async (caseIds) => {
  const params = {
    caseIds
  };
  const url = `${CM_BASE_URL}/cases/validCaseStatus`;

  return axios.post(url, params).then((res) => {
    if (res.status !== 200) {
      throw { message: 'Failed to fetch allowed case status transitions' };
    }
    return res.data;
  });
};

export const submitCasesStatus = async ({ caseIds, status }) => {
  const requestBody = {
    caseId: caseIds,
    status
  };

  const url = `${CM_BASE_URL}/cases/updateCaseStatus`;

  return axios.put(url, requestBody).then((res) => {
    if (res.status !== 200 && res.data.status !== 'success') {
      throw { message: 'Failed to update case status' };
    }
    return res.data;
  });
};

export const assignCaseToUser = async ({ caseIds, user }) => {
  const requestBody = {
    caseIds,
    user
  };

  const url = `${CM_BASE_URL}/cases/assignToUser`;

  const res = await axios.put(url, requestBody);
  if (res.status !== 200 && res.data.status !== 'success') {
    const error = new Error(res.data?.msg || 'Something went wrong. Please try again after sometime.');
    error.response = res;
    throw error;
  }
  return res.data;
};

export const updateCaseName = async ({ caseId, caseName }) => {
  const url = `${CM_BASE_URL}/cases/${caseId}/name`;

  const params = {
    name: caseName
  };

  return axios.put(url, params).then((res) => {
    if (res.status !== 200 && res.data.status !== 'success') {
      throw { message: 'Failed to update the case name' };
    }
    return res.data;
  });
};

export const createCase = async ({
  name,
  note,
  customers,
  dept,
  customFields
}) => {
  const requestBody = {
    name,
    note,
    customers,
    dept,
    customFields
  };

  const url = `${CM_BASE_URL}/cases`;

  return axios.post(url, requestBody).then((res) => {
    if (res.status !== 200 || !res.data.caseId) {
      const error = res?.data?.error;
      throw new Error(error ?? 'Error creating a case');
    }
    return res.data;
  });
};

export const updateCase = async (caseDetails) => {
  const url = `${CM_BASE_URL}/cases/${caseDetails.caseId}`;
  const requestBody = omit(caseDetails, ['caseId']);

  return axios.put(url, requestBody).then((res) => {
    if (res.status !== 200 || !res.data.caseId) {
      const error = res?.data?.error;
      throw new Error(error ?? 'Error updating a case');
    }
    return res.data;
  });
};

export const downloadSTRReport = (
  documentId,
  caseId,
  onGetStrSuccess,
  onGetStrError
) => {
  const fileUrl = `${CM_BASE_URL}/cases/getSTR?docId=${documentId}`;
  axios
    .get(fileUrl, {
      responseType: 'blob'
    })
    .then((response) => {
      //status 202: download still in progress
      if (response.status === 202) {
        setTimeout(
          () =>
            downloadSTRReport(
              documentId,
              caseId,
              onGetStrSuccess,
              onGetStrError
            ),
          STR_POLLING_INTERVAL_MS
        );
      } else if (response.status === 200) {
        downloadFile(response.data, `Investigation Report - ${caseId}.pdf`);
        onGetStrSuccess(caseId);
      } else {
        onGetStrError();
        throw { message: 'Failed to download STR' };
      }
    });
};

export const generateSTRReport = (caseId, alertIds, onCreateStrSuccess) => {
  const requestBody = {
    caseId: parseInt(caseId),
    alertIds
  };
  const url = `${CM_BASE_URL}/cases/createSTR`;
  axios
    .post(url, requestBody, { headers: { 'Time-Zone': getLocalTimezone() } })
    .then((res) => {
      if (res.status !== 200 || !res.data.documentId) {
        throw { message: 'Failed to generate STR' };
      } else {
        onCreateStrSuccess(res.data.documentId, caseId);
      }
    });
};

export const fetchSTRCaseReportByCaseId = async (payload) => {
  const currentPage = payload.currentHistoryPage || 1;
  const limit = payload.currentPageSize || HISTORY_ITEMS_PER_PAGE;

  let params = {};

  if (['STR', 'CTR'].includes(payload.type)) {
    params = { offset: (currentPage - 1) * limit, limit: limit };
  }
  if (payload.reportId) {
    params.reportId = payload.reportId;
  }
  if (payload.caseId) {
    params.caseId = payload.caseId;
  }
  let url;
  if (payload.reportId) {
    url = `${CM_BASE_URL}/reports/getCTR`;
  } else {
    url = `${AMLS_REPORT_BASE_URL}/`;
  }
  const res = await axios.get(url, {
    params,
    headers: { 'Time-Zone': getLocalTimezone() }
  });
  if (res.status === 404) {
    return null;
  }
  if (res.status !== 200) {
    throw new Error(res?.data?.error ?? 'Failed to fetch STR details');
  }
  return res.data;
};

export const updateSTRCaseReportByCaseId = async (reportUpatedFields) => {
  const url = `${CM_BASE_URL}/reports/StrOrCtr/update`;
  const res = await axios.post(url, reportUpatedFields);
  if (res.status !== 200) {
    throw new Error(res?.data?.error ?? 'Failed to update the fields');
  }
  return res.data;
};

export const downloadReportViaS3 = async (reportId, format) => {
  // Fetch a fresh presigned S3 URL from the backend — no streaming through the server
  const params = format ? { format: `${format}_CSV` } : {};
  const res = await axios.get(`${AMLS_REPORT_BASE_URL}/${reportId}/file`, {
    params
  });
  const { downloadUrl } = res.data || {};
  if (downloadUrl) {
    const a = document.createElement('a');
    a.download = '';
    a.href = downloadUrl;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    await new Promise((r) => setTimeout(r, 100));
    a.remove();
  } else {
    throw new Error('No download URL returned for report');
  }
};

export const fetchReportList = async (payload) => {
  const {
    reportType,
    page,
    pageSize,
    sortBy,
    sortOrder,
    status,
    format,
    fromDate,
    toDate,
    search
  } = payload;
  const url = `${AMLS_REPORT_BASE_URL}/list`;
  const res = await axios.get(url, {
    params: {
      reportType,
      page,
      pageSize,
      sortBy,
      sortOrder,
      status,
      format,
      fromDate,
      toDate,
      search
    }
  });
  if (res.status !== 200) {
    throw new Error(res?.data?.error ?? 'Failed to fetch report list');
  }
  return res.data;
};

export const fetchReportMetadata = async (payload) => {
  const { caseId, reportId, format } = payload;
  const url = `${AMLS_REPORT_BASE_URL}/`;
  const res = await axios.get(url, {
    params: { caseId, reportId, format }
  });
  if (res.status !== 200) {
    throw new Error(res?.data?.error ?? 'Failed to fetch report metadata');
  }
  return res.data;
};

export const updateReportingInstitution = async (payload) => {
  const { reportId, ...data } = payload;
  const url = `${AMLS_REPORT_BASE_URL}/${reportId}`;
  const res = await axios.post(url, data);
  if (res.status !== 200) {
    throw new Error(
      res?.data?.message ?? res?.data?.error ?? 'Failed to update reporting institution'
    );
  }
  return res.data;
};

export const updateTransactionList = async (payload) => {
  const { reportId, ...data } = payload;
  const url = `${AMLS_REPORT_BASE_URL}/${reportId}/transactions`;
  const res = await axios.post(url, data);
  if (res.status !== 200) {
    throw new Error(res?.data?.error ?? 'Failed to update transactions');
  }
  return res.data;
};

export const fetchTransactions = async (payload) => {
  const { reportId, format, page, pageSize, isDownload, filterParams, signal } =
    payload;
  const url = `${AMLS_REPORT_BASE_URL}/${reportId}/transactions`;
  const config = {
    params: { format, page, pageSize, download: isDownload, ...filterParams },
    signal
  };
  if (isDownload) {
    config.responseType = 'blob';
  }
  const res = await axios.get(url, config);
  if (res.status !== 200) {
    throw new Error(res?.data?.error ?? 'Failed to fetch transactions');
  }
  if (isDownload) {
    const disposition = res.headers['content-disposition'] || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    const fileName = match ? match[1] : `Transactions_${reportId}.csv`;
    const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(link.href);
    return { downloaded: true };
  }
  return res.data;
};

export const fetchParties = async (payload) => {
  const { reportId, format, page, pageSize, filterParams, signal } = payload;
  const url = `${AMLS_REPORT_BASE_URL}/${reportId}/parties`;
  const res = await axios.get(url, {
    params: { format, page, pageSize, ...filterParams },
    signal
  });
  if (res.status !== 200) {
    throw new Error(res?.data?.error ?? 'Failed to fetch parties');
  }
  return res.data;
};

export const updatePartiesList = async (payload) => {
  const { reportId, ...data } = payload;
  const url = `${AMLS_REPORT_BASE_URL}/${reportId}/parties`;
  const res = await axios.post(url, data);
  if (res.status !== 200) {
    throw new Error(res?.data?.error ?? 'Failed to update parties');
  }
  return res.data;
};

export const deleteSTRReportByCaseId = async (caseId) => {
  const url = `${AMLS_REPORT_BASE_URL}/byCaseId/${caseId}`;
  console.log('[deleteSTRReportByCaseId] calling DELETE', url);
  const res = await axios.delete(url);
  console.log('[deleteSTRReportByCaseId] response status:', res.status, '| data:', res.data);
  if (res.status !== 200) {
    throw new Error(res?.data?.message ?? 'Failed to delete STR report');
  }
  return res.data;
};

export const createSTRCaseReportByCaseId = async ({ caseId, alertIds }) => {
  const requestBody = {
    caseId: parseInt(caseId),
    alertIds
  };
  const url = `${CM_BASE_URL}/reports/createSTR`;
  const res = await axios.post(url, requestBody, {
    headers: { 'Time-Zone': getLocalTimezone() }
  });

  if (res.status !== 200) {
    throw new Error(res?.data?.error ?? 'Failed to generate STR');
  }
  return res.data;
};

export const downloadReport = async (payload) => {
  const uuid = uniqueId();
  showFileDownloadingNotification({ key: uuid, fileName: '' });
  const url = `${CM_BASE_URL}/reports/StrOrCtr/download`;
  await axios
    .get(url, {
      params: payload,
      responseType: 'blob',
      headers: { 'Time-Zone': 'UTC' },
      timeout: 0
    })
    .then((res) => {
      if (res.status === 200) {
        const fileName = getFileNameFromContentDisposition(
          res.headers['content-disposition']
        );
        showFileDownloadingNotification({ key: uuid, fileName });
        try {
          // If CSV, prepend UTF-8 BOM to preserve correct encoding in Excel and viewers
          const contentType = res.headers['content-type'] || '';
          let data = res.data;
          if (
            /\.csv$/i.test(fileName || '') ||
            contentType.startsWith('text/csv')
          ) {
            const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
            data = new Blob([bom, res.data], {
              type: 'text/csv;charset=UTF-8'
            });
          }
          downloadFile(data, fileName);
          showFileDownloadSucessNotification({ key: uuid, fileName });
        } catch (e) {
          showFileDownloadFailedNotification({ key: uuid, fileName });
        }
      } else {
        showFileDownloadFailedNotification({
          key: uuid,
          fileName: `'${filters.reportTypeLabel}' report`
        });
      }
    })
    .catch((err) => {
      showFileDownloadFailedNotification({ key: uuid, fileName: '' });
    });
};
